import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Collapse,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Update as UpdateIcon,
  Schedule as ScheduleIcon,
  MusicNote as MusicNoteIcon,
} from '@mui/icons-material';
import {
  CompositeMetadataAnalysis,
  ComponentMetadataComparison,
} from '../../services/componentMetadataService';

interface ComponentChangesBannerProps {
  analysis: CompositeMetadataAnalysis;
  context: 'view' | 'edit';
  onUpdate: () => void;
  onDismiss: () => void;
  loading?: boolean;
}

/**
 * Hybrid Info Banner + Expandable Details component for component change detection
 * Replaces modal dialog with inline, non-intrusive banner that can expand to show details
 */
const ComponentChangesBanner: React.FC<ComponentChangesBannerProps> = ({
  analysis,
  context,
  onUpdate,
  onDismiss,
  loading = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!analysis.hasAnyChanges) {
    return null;
  }

  const changesCount = analysis.componentComparisons.filter(
    comp => comp.hasChanges
  ).length;
  const totalComponents = analysis.componentComparisons.length;

  const getContextualText = () => {
    if (context === 'view') {
      return {
        title: 'Component Metadata Updated',
        description: `${changesCount} component${
          changesCount > 1 ? 's have' : ' has'
        } been updated since this composite was created`,
        updateButton: 'Update Composite',
        keepButton: 'Keep Current Metadata',
      };
    } else {
      return {
        title: 'Component Metadata Updated',
        description: `${changesCount} component${
          changesCount > 1 ? 's have' : ' has'
        } been updated since this composite was created`,
        updateButton: 'Update Composite',
        keepButton: 'Keep Current Metadata',
      };
    }
  };

  const contextText = getContextualText();

  const renderComponentChange = (comparison: ComponentMetadataComparison) => {
    if (!comparison.hasChanges) return null;

    const displayChanges = comparison.changes.map(change => {
      const fieldName = change.field;
      const formattedFieldName =
        fieldName === 'songName'
          ? 'Song Name'
          : fieldName === 'artistName'
          ? 'Artist'
          : fieldName === 'albumName'
          ? 'Album'
          : fieldName === 'albumArtUrl'
          ? 'Album Art'
          : fieldName === 'bpm'
          ? 'BPM'
          : fieldName === 'genre'
          ? 'Genre'
          : fieldName;

      if (fieldName === 'albumArtUrl') {
        return 'Album Art: Updated';
      }

      return `${formattedFieldName}: ${change.oldValue} → ${change.newValue}`;
    });

    return (
      <Box key={comparison.componentId} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <MusicNoteIcon sx={{ color: 'primary.main', mr: 1, fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Primary Song Component
          </Typography>
          <Chip
            icon={<ScheduleIcon />}
            label="Updated recently"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ ml: 'auto', fontSize: '0.7rem' }}
          />
        </Box>
        <Box sx={{ pl: 3 }}>
          {displayChanges.map((change, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 0.5,
                '&::before': {
                  content: '"• "',
                  color: 'primary.main',
                  fontWeight: 'bold',
                },
              }}
            >
              {change}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Alert
      severity="warning"
      sx={{
        mb: 3,
        '& .MuiAlert-icon': {
          color: 'warning.main',
        },
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
    >
      {/* Header Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <UpdateIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {contextText.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: 'warning.dark' }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {contextText.description}
      </Typography>

      {/* Expandable Details */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ mb: 2 }}>
            <Chip
              label={`Detailed Changes (${changesCount} changes across ${totalComponents} component${
                totalComponents > 1 ? 's' : ''
              })`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Divider>

          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {analysis.componentComparisons
              .filter(comp => comp.hasChanges)
              .map(comparison => renderComponentChange(comparison))}
          </Box>
        </Box>
      </Collapse>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onDismiss}
          disabled={loading}
          size="small"
        >
          {contextText.keepButton}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onUpdate}
          disabled={loading}
          startIcon={loading ? undefined : <UpdateIcon />}
          size="small"
          sx={{
            bgcolor: 'warning.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'warning.dark',
            },
          }}
        >
          {loading ? 'Updating...' : contextText.updateButton}
        </Button>
      </Box>

      {/* Note */}
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          bgcolor: 'rgba(237, 108, 2, 0.1)',
          borderRadius: 1,
          border: '1px solid rgba(237, 108, 2, 0.2)',
        }}
      >
        <Typography
          variant="caption"
          color="warning.dark"
          sx={{ fontWeight: 500 }}
        >
          <strong>Note:</strong> Updating will replace the current metadata in
          this composite with the latest values from the component assets. This
          action cannot be undone.
        </Typography>
      </Box>
    </Alert>
  );
};

export default ComponentChangesBanner;
